import type { TeamChoice } from '@ghp/domain'
import { Button, Field, Input, Modal } from '@ghp/ui'
import { useEffect, useId, useState } from 'react'

import {
  CONNECTION_EXPLANATION,
  CONNECTION_ICON,
  CONNECTION_LABEL,
  type ConnectionStatus,
} from '../../../app/connection'
import type { AuthStore } from '../auth-store'
import type { TerrainSession } from '../session'
import { failureFor, faultOf, incompleteEntry, teamLabel, type LoginFailure } from '../sign-in'
import s from './ConnectionPanel.module.css'

export interface ConnectionPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  status: ConnectionStatus
  session: TerrainSession
  /** La reconnexion, telle que l'écran de connexion l'appelle : mêmes deux temps (M6). */
  signIn: AuthStore['signIn']
  /**
   * Une passe de synchro complète — rafraîchissement, file, descentes. **Ne rejette jamais**
   * (`FieldContext.requestSync`), d'où l'absence de `catch` autour de son appel.
   */
  onRetry: () => Promise<void>
}

/**
 * **Le panneau de connexion du rail** — ce que l'icône d'état ouvre.
 *
 * Il répond à trois défauts constatés ensemble sur la tablette, qui n'en faisaient qu'un pour
 * l'agent : le rail affichait « En ligne » pendant que la déconnexion se disait non confirmée,
 * l'icône n'était qu'un décor, et une session refusée par le serveur n'offrait **aucun geste**
 * — il fallait deviner qu'un rechargement de page ne servirait à rien (#141).
 *
 * D'où trois choses ici, et rien d'autre :
 *
 * 1. **l'état, en toutes lettres**, celui-là même que le rail abrège et que la déconnexion
 *    invoque pour se fermer — une seule source (`app/connection.ts`), donc plus de contradiction
 *    possible entre deux coins de l'écran ;
 * 2. **un essai immédiat**, qui rejoue la passe de synchro au lieu d'attendre le tour de
 *    minuteur suivant. C'est la version « connexion » du bouton « Synchroniser » du journal, et
 *    elle passe par la **passe complète** pour la même raison : un jeton périmé se renouvelle
 *    avant l'envoi plutôt que d'échouer en silence ;
 * 3. **la ressaisie des identifiants**, et seulement quand le serveur a dit que c'était la seule
 *    issue. La proposer en permanence ferait retaper un mot de passe pour une coupure réseau.
 *
 * ⚠️ **La reconnexion se fait ici, pas à l'écran de connexion**, et c'est l'essentiel : passer
 * par `/login` suppose une déconnexion, qui efface la session locale — donc le périmètre
 * d'équipe sous lequel les saisies en cours sont lisibles. Le départ, lui, est **ouvert** dans
 * cet état depuis #222, et doit le rester : c'est l'issue quand la tablette change de main. Mais
 * il coûte la tournée, la ressaisie non — d'où les deux chemins, et l'ordre entre eux.
 *
 * ⚠️ **La persistance du stockage n'est pas redemandée ici**, contrairement à l'écran de
 * connexion. `navigator.storage.persist()` se demande « au moment de la connexion » (D22) parce
 * que c'est le dernier instant où l'agent est au dépôt, avec du réseau et quelqu'un à prévenir ;
 * une ressaisie en pleine tournée n'est pas ce moment-là, et un avertissement de stockage devant
 * une maison ne porte aucune action possible.
 */
export function ConnectionPanel({
  open,
  onOpenChange,
  status,
  session,
  signIn,
  onRetry,
}: ConnectionPanelProps) {
  const failureId = useId()

  const knownLogin = session.state === 'signed_out' ? '' : session.user.login
  const [login, setLogin] = useState(knownLogin)
  const [password, setPassword] = useState('')
  const [teams, setTeams] = useState<TeamChoice[] | null>(null)
  const [failure, setFailure] = useState<LoginFailure | null>(null)
  const [pending, setPending] = useState(false)
  const [retrying, setRetrying] = useState(false)

  // Fermer désarme : un mot de passe à moitié tapé, une liste d'équipes ou un refus ne doivent
  // pas attendre la réouverture suivante — c'est le même raisonnement que la confirmation de
  // déconnexion, qu'un changement d'écran désarme.
  useEffect(() => {
    if (open) return

    setPassword('')
    setTeams(null)
    setFailure(null)
  }, [open])

  async function retry(): Promise<void> {
    setRetrying(true)
    await onRetry()
    setRetrying(false)
  }

  async function submit(teamId?: TeamChoice['id']): Promise<void> {
    const incomplete = incompleteEntry({ login, password })
    setFailure(incomplete)
    if (incomplete !== null) return

    setPending(true)
    try {
      const result = await signIn({ login, password, teamId })
      if (result.outcome === 'team_selection_required') {
        setTeams(result.teams)
        return
      }

      /*
       * La session est de nouveau en ligne, et **rien n'est à vider** : le périmètre d'équipe
       * vit dans les clés de cache (`offline/query-keys.ts`), donc une équipe entrante lit sous
       * d'autres clés que la sortante. C'est ce qui permet de rouvrir la session sur place
       * plutôt que de repasser par la déconnexion, qui, elle, vide le cache faute de recharger.
       */
      setPassword('')
      onOpenChange(false)
    } catch (thrown) {
      setFailure(failureFor(thrown))
    } finally {
      setPending(false)
    }
  }

  const Icon = CONNECTION_ICON[status]

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      icon={<Icon size={18} />}
      title={`Connexion · ${CONNECTION_LABEL[status]}`}
      footer={
        <>
          <Button tone="neutral" disabled={retrying} onClick={() => void retry()}>
            {retrying ? 'Essai en cours…' : 'Réessayer maintenant'}
          </Button>
          <Button tone="ghost" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </>
      }
    >
      <div className={s.body}>
        {/* `role="status"` : l'état change sous les yeux de l'agent — au retour du réseau, ou
            quand l'essai ci-dessous aboutit — et il doit s'annoncer sans reprise de focus. */}
        <p className={s.state} role="status">
          {CONNECTION_EXPLANATION[status]}
        </p>

        {status === 'sign_in_required' &&
          (teams === null ? (
            <form
              className={s.form}
              onSubmit={(event) => {
                event.preventDefault()
                void submit()
              }}
            >
              <Field label="Identifiant">
                {({ id }) => (
                  <Input
                    id={id}
                    name="login"
                    autoComplete="username"
                    autoCapitalize="none"
                    value={login}
                    onValueChange={setLogin}
                    {...faultOf(failure, 'login', failureId)}
                  />
                )}
              </Field>
              <Field label="Mot de passe">
                {({ id }) => (
                  <Input
                    id={id}
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onValueChange={setPassword}
                    {...faultOf(failure, 'password', failureId)}
                  />
                )}
              </Field>
              <Button tone="brand" type="submit" fullWidth disabled={pending}>
                {pending ? 'Connexion…' : 'Rouvrir la session'}
              </Button>
            </form>
          ) : (
            /*
             * Second temps de M6 : l'agent appartient à plusieurs équipes, aucun jeton n'a été
             * émis. L'équipe de la session **n'est pas présélectionnée** — l'identifiant saisi
             * peut être un autre compte, et rappeler l'équipe précédente ferait demander au
             * serveur un droit que ce compte n'a peut-être pas.
             */
            <div className={s.teams}>
              <p>{pending ? 'Connexion à l’équipe…' : 'Choisir l’équipe de la tournée :'}</p>
              {teams.map((team) => (
                <Button
                  key={team.id}
                  tone="neutral"
                  fullWidth
                  disabled={pending}
                  onClick={() => void submit(team.id)}
                >
                  {teamLabel(team, teams)}
                </Button>
              ))}
            </div>
          ))}

        {failure !== null && (
          <p id={failureId} className={s.error} role="alert">
            {failure.message}
          </p>
        )}
      </div>
    </Modal>
  )
}
